import { StateCreator } from "zustand";
import api from "../utils/api";
import { handleApiError } from "../utils/errorHandler";
import {
  setAuthCookies,
  clearAuthCookies,
  getAuthCookies,
} from "../utils/cookies";
import {
  setUserInfoInLocalStorage,
  clearUserInfoFromLocalStorage,
} from "../utils/localStorage";

export interface LoginAuthState {
  phoneNumber: string;
  countryCode: string;
  user: { token: string } | null;
  companyName: string | null;
  email: string | null;
  logo: string | null;
  error: string | null;
  loading: boolean;
  updatePhoneNumber: (phoneNumber: string) => void;
  updateCountryCode: (countryCode: string) => void;
  requestOtp: () => Promise<void>;
  resendOtpLogin: () => Promise<void>;
  validateOtp: (otp: string) => Promise<boolean>;
  logout: () => void;
}

export const createLoginAuthSlice: StateCreator<LoginAuthState> = (set, get) => ({
  phoneNumber: "",
  countryCode: "",
  companyName: null,
  email: null,
  logo: null,
  user: getAuthCookies() || null,
  error: null,
  loading: false,

  updatePhoneNumber: (phoneNumber: string) => set({ phoneNumber }),
  updateCountryCode: (countryCode: string) => set({ countryCode }),

  requestOtp: async () => {
    set({ loading: true, error: null });
    try {
      const { phoneNumber, countryCode } = get();
      await api.post("/auth/sendOTPtoLogin", {
        phoneNumber,
        countryCode,
      });
    } catch (error: any) {
      set({ error: handleApiError(error) });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  resendOtpLogin: async () => {
    set({ loading: true, error: null });
    try {
      const { phoneNumber, countryCode } = get();
      await api.post("/auth/resendOTP", {
        phoneNumber,
        countryCode,
      });
    } catch (error: any) {
      set({ error: handleApiError(error) });
    } finally {
      set({ loading: false });
    }
  },

  validateOtp: async (otp: string) => {
    set({ loading: true, error: null });
    try {
      const { phoneNumber, countryCode } = get();

      const response = await api.post("/auth/verifyOTPForLogin", {
        otp,
        phoneNumber,
        countryCode,
      });

      const { token, companyName, email, logo } = response.data;

      setAuthCookies({
        token,
        role: null,
      });

      setUserInfoInLocalStorage(companyName, email, logo);

      set({
        user: { token },
        companyName,
        email,
        logo,
      });

      return true;
    } catch (error: any) {
      set({ error: handleApiError(error) });
      clearAuthCookies();
      set({ user: null });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  logout: () => {
    clearAuthCookies();
    clearUserInfoFromLocalStorage();
    set({
      user: null,
      companyName: null,
      email: null,
      logo: null,
    });
  },
});
